/**
 * Keeping both fighters in frame.
 *
 * The boot camera was a fixed three-quarter view onto the origin, which works
 * exactly as long as there is one fighter standing on it. With two, the camera
 * has to be derived from where they actually are: it watches from the side of
 * the line between them, backed off far enough that the pair plus some air
 * fits inside the frustum.
 *
 * `solveFraming` is pure trigonometry on plain objects — no three.js, no
 * camera — so the framing can be tested at a dozen separations and aspect
 * ratios without a renderer. `frameCamera` is the thin part that moves a real
 * camera to the answer.
 *
 * Still no orbit controls, for the same reason as before: the shipping game has
 * a directed camera, and a free one hides framing bugs instead of showing them.
 */

export const FRAMING = Object.freeze({
  /** Metres of clear space beyond the outermost fighter. */
  margin: 1.4,
  /** Vertical extent kept in frame: a ~1.75m fighter with head and foot room. */
  subjectHeight: 2.2,
  /** Height above the floor the camera aims at. */
  lookHeight: 1,
  /** Downward tilt, radians. Enough to read the floor without looking down on it. */
  pitch: 0.22,
  /**
   * Rotation off a pure side-on view, radians. Square-on is the easiest angle
   * to frame and the dullest to look at; this keeps the three-quarter view the
   * boot scene was composed around.
   */
  yaw: 0.45,
  minDistance: 3,
  maxDistance: 11,
  /**
   * The half of the arena the camera is allowed to watch from, as a direction
   * on the floor. `null` lets it stand wherever the geometry puts it.
   *
   * A square room does not care: the shot is derived from the line between the
   * fighters, and either side of that line shows the same walls. A subway
   * platform cares a great deal. It is tiled wall down one side and open track
   * down the other, and which side the camera lands on is decided by the order
   * the two fighters happen to be standing in — so it *flips*, mid-fight, the
   * moment they walk past each other, and half the time it flips to behind the
   * wall. Pinning the hemisphere costs one dot product and makes the shot a
   * property of the arena rather than of who is currently on the left.
   */
  side: null,
});

/**
 * Where the camera has to stand to hold `points` in frame.
 *
 * Returns plain `{ position, target, distance }`, or null for no subjects.
 * `fovY` is vertical field of view in radians; `aspect` is width / height.
 */
export function solveFraming(points, { fovY, aspect, ...overrides } = {}) {
  if (!points?.length) return null;
  // A viewport with no area gives an aspect of 0/0. Solving from that puts the
  // camera at NaN, and a NaN camera never recovers once the window is resized
  // back — better to decline and leave the last good pose alone.
  if (!(fovY > 0) || !(aspect > 0) || !Number.isFinite(aspect)) return null;

  const config = { ...FRAMING, ...overrides };

  // The midpoint on the floor, and the axis the fighters are strung along.
  const center = { x: 0, z: 0 };
  for (const point of points) {
    center.x += point.x / points.length;
    center.z += point.z / points.length;
  }

  const { axis, separation } = spread(points, center);

  // Watch from the side of that axis, then twist off it for the three-quarter.
  let side = rotateY({ x: -axis.z, z: axis.x }, config.yaw);
  // The axis points from one fighter to the other, and which one is "one" is
  // whatever order they were handed over in. An arena that has a front keeps
  // the camera in front of it.
  if (config.side && side.x * config.side.x + side.z * config.side.z < 0) {
    side = { x: -side.x, z: -side.z };
  }

  // Turning off square-on foreshortens the gap between the fighters, so less
  // distance is needed to hold it — the cosine is the whole of that saving.
  const halfWidth = (separation / 2) * Math.cos(config.yaw) + config.margin;
  const tanY = Math.tan(fovY / 2);
  const tanX = tanY * aspect;

  const distance = clamp(
    Math.max(halfWidth / tanX, config.subjectHeight / 2 / tanY),
    config.minDistance,
    config.maxDistance
  );

  // The director's two asks (step 98, `director.js`): stand closer by
  // `push`, and look a little towards `aim`. Both default to nothing.
  const pushed = distance * (config.push ?? 1);
  const pull = config.aim?.weight ?? 0;
  const target = {
    x: center.x + (pull ? (config.aim.x - center.x) * pull : 0),
    y: config.lookHeight,
    z: center.z + (pull ? (config.aim.z - center.z) * pull : 0),
  };
  const flat = Math.cos(config.pitch);
  return {
    target,
    distance: pushed,
    position: within(config, {
      x: target.x + side.x * pushed * flat,
      y: target.y + Math.sin(config.pitch) * pushed,
      z: target.z + side.z * pushed * flat,
    }),
  };
}

/**
 * The box the camera may stand in, when the arena declares one.
 *
 * `minX`..`maxZ` and `minY`/`maxY` on an arena's `camera`. The subway has
 * declared them since step 31 and nothing read them, which was harmless on a
 * platform with the sky of a station roof over it. Under a garage's ceiling it
 * is not: the solved shot backs off and climbs, and the camera ends up above
 * the slab it is supposed to be under, looking at the top of it.
 *
 * Clamped per axis and the aim left alone, so a camera that is held down or in
 * looks a little flatter or a little closer rather than somewhere else.
 */
function within(config, position) {
  for (const axis of ['x', 'y', 'z']) {
    const low = config[`min${axis.toUpperCase()}`];
    const high = config[`max${axis.toUpperCase()}`];
    if (Number.isFinite(low)) position[axis] = Math.max(low, position[axis]);
    if (Number.isFinite(high)) position[axis] = Math.min(high, position[axis]);
  }
  return position;
}

/**
 * Moves a three.js camera onto the solved framing. Returns the solution.
 *
 * `shake` is added to the solved position and nothing else — the camera still
 * looks at the same point, so a few centimetres of jitter comes out as a small
 * swivel rather than as the whole shot sliding. It is deliberately outside
 * `solveFraming`: the framing is a fact about where the fighters are, and the
 * shake is a lie told on top of it for a quarter of a second.
 */
export function frameCamera(camera, points, { shake, drift, look, ...overrides } = {}) {
  const shot = solveFraming(points, {
    fovY: (camera.fov * Math.PI) / 180,
    aspect: camera.aspect,
    ...overrides,
  });
  if (!shot) return null;

  // The director's drift rides on the position like the shake does, and its
  // own small wander of the aim; neither is part of the solved shot.
  camera.position.set(
    shot.position.x + (shake?.x ?? 0) + (drift?.x ?? 0),
    shot.position.y + (shake?.y ?? 0) + (drift?.y ?? 0),
    shot.position.z + (shake?.z ?? 0) + (drift?.z ?? 0)
  );
  camera.lookAt(shot.target.x + (look?.x ?? 0), shot.target.y + (look?.y ?? 0), shot.target.z + (look?.z ?? 0));
  return shot;
}

/**
 * The widest gap between the subjects, and the horizontal direction of it.
 * Two fighters is the case that matters, but taking the extremes means a third
 * body in the scene later widens the shot rather than being cropped out of it.
 */
function spread(points, center) {
  const fallback = { axis: { x: 1, z: 0 }, separation: 0 };
  if (points.length < 2) return fallback;

  let axis = null;
  let longest = 0;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const dx = points[j].x - points[i].x;
      const dz = points[j].z - points[i].z;
      const gap = Math.hypot(dx, dz);
      if (gap > longest) {
        longest = gap;
        axis = { x: dx / gap, z: dz / gap };
      }
    }
  }
  // Everyone standing on the same spot leaves no axis to work from.
  if (!axis) return fallback;

  // Measure the spread around the centre along that axis, so an off-centre
  // third fighter still widens the frame.
  let reach = 0;
  for (const point of points) {
    reach = Math.max(reach, Math.abs((point.x - center.x) * axis.x + (point.z - center.z) * axis.z));
  }
  return { axis, separation: reach * 2 };
}

function rotateY(v, angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: v.x * cos + v.z * sin, z: -v.x * sin + v.z * cos };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
